import MuiSlider, { SliderProps as MuiSliderProps } from "@mui/material/Slider";
import React, { ChangeEvent, FunctionComponent } from "react";
import { GridRow } from "../grid/GridContainer";
import TextField, { TextFieldProps } from "../textfield/TextField";

export interface SliderRangeProps extends Omit<MuiSliderProps, "max" | "min" | "onChange" | "value"> {
    /** includes a textField component to reflect the slider value */
    includeTextFields?: boolean;

    /** max value for the slider and input */
    max: number;

    /** min value for the slider and input */
    min: number;

    /** onChange function that will sync the slider and textField values */
    onChange: (value: [number, number], activeThumb: number) => void;

    /** sets spacing around the slider*/
    spacingAround?: 1 | 2 | 3 | 4 | 5;

    /** props for textField */
    textFieldProps?: Partial<Omit<TextFieldProps, "onBlur" | "onChange" | "type" | "value">>;

    /** array of numbers for the range */
    value: [number, number];
};;

const INPUT_STYLES = {
	width: 50,
	/** Removes the up & down dials */
	"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
		display: "none",
		WebkitAppearance: "none",
		margin: 0,
	},
	"& input[type=number]": {
		MozAppearance: "textfield",
	},
};

const SliderRange: FunctionComponent<SliderRangeProps> = (props) => {
	const { includeTextFields, textFieldProps, value, min, max, step, spacingAround, onChange, ...otherProps } = props;
	const { inputProps, ...otherTextFieldProps } = textFieldProps || {};
	const invalidMinValue = value[0] < min || value[0] > max;
	const invalidMaxValue = value[1] < min || value[1] > max;

	const onInputChange = (newValue: number, activeThumb: 0 | 1) => {
		/** if the value is less than the min, set it to the min. if it's greater than the max, set it to the max */
		activeThumb === 0
			? onChange([Math.min(newValue, value[1] - min), value[1]], activeThumb)
			: onChange([value[0], Math.max(newValue, value[0] + min)], activeThumb);
	};

	const onBlur = (activeThumb: 0 | 1) => {
		if (value[activeThumb] < min) {
			onChange([min, value[1]], activeThumb);
		} else if (value[activeThumb] > max) {
			onChange([value[0], max], activeThumb);
		}
	};

	return (
		<GridRow
			grow
			growChild={includeTextFields ? 1 : 0}
			spacing={4}
			spacingAround={spacingAround ? spacingAround : 1}
			sx={{
				/** Allows for the thumb hover state to overflow */
				"& .MuiGrid-root": {
					overflow: "initial",

				},
			}}
		>
			{includeTextFields && (
				<TextField
					error={invalidMinValue}
					inputProps={{
						max,
						min,
						step,
						...inputProps,
					}}
					onBlur={() => onBlur(0)}
					onChange={(event: ChangeEvent<HTMLInputElement>) => onInputChange(Number(event.target.value), 0)}
					sx={INPUT_STYLES}
					type="number"
					value={value[0]}
					{...otherTextFieldProps}
				/>
			)}
			<MuiSlider
				max={max}
				min={min}
				onChange={(event: Event, value: [number, number], activeThumb: number) => onChange(value, activeThumb)}
				step={step}
				value={value}
				{...otherProps}
			/>
			{includeTextFields && (
				<TextField
					error={invalidMaxValue}
					inputProps={{
						max,
						min,
						step,
						...inputProps,
					}}
					onBlur={() => onBlur(1)}
					onChange={(event: ChangeEvent<HTMLInputElement>) => onInputChange(Number(event.target.value), 1)}
					sx={INPUT_STYLES}
					type="number"
					value={value[1]}
					{...otherTextFieldProps}
				/>
			)}
		</GridRow>
	);
};


export default SliderRange;
